import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { fetchWithHeaders } from "../api/fetchWithHeaders";
import { useToastStore } from "./toastStore";

export enum FeatureFlag {
	VISUALIZATION_AGENT = "VISUALIZATION_AGENT",
	CUSTOM_TAB = "CUSTOM_TAB",
}

interface FeatureFlagsState {
	featureFlags: string[];
	availableFlags: string[];
	loading: boolean;
	error: string | null;
	featureFlagStatus: Record<string, Record<string, boolean>>;

	// Actions
	fetchFeatureFlags: (negotiationId: number) => Promise<void>;
	fetchAvailableFlags: () => Promise<void>;
	checkFeatureFlag: (negotiationId: number, featureFlag: string) => Promise<boolean>;
	setFeatureFlag: (
		negotiationId: number,
		featureFlag: string,
		enabled: boolean
	) => Promise<boolean>;
	hasFeature: (featureFlag: string) => boolean;
	isFeatureEnabled: (negotiationId: number, featureFlag: string) => boolean;
	reset: () => void;
}

export const useFeatureFlagsStore = create<FeatureFlagsState>()(
	devtools(
		(set, get) => ({
			featureFlags: [],
			availableFlags: Object.values(FeatureFlag),
			loading: false,
			error: null,
			featureFlagStatus: {},

			fetchFeatureFlags: async (negotiationId: number) => {
				const url = import.meta.env.VITE_API_BASE_URL;
				const { error } = useToastStore.getState();

				set({ loading: true, error: null });

				try {
					const response = await fetchWithHeaders(
						`${url}api/v1/feature-flags/${negotiationId}`,
						{ method: "GET" }
					);

					const data = await response.json();
					set({
						featureFlags: data.feature_flags || [],
						loading: false,
					});
				} catch (err) {
					const errorMessage =
						err instanceof Error
							? err.message
							: "Failed to fetch feature flags";
					error("Error", errorMessage);
					set({ error: errorMessage, loading: false });
				}
			},

			checkFeatureFlag: async (negotiationId: number, featureFlag: string) => {
				const url = import.meta.env.VITE_API_BASE_URL;
				const { error } = useToastStore.getState();

				try {
					const response = await fetchWithHeaders(
						`${url}api/v1/feature-flags/${negotiationId}/check?feature_flag=${featureFlag}`,
						{ method: "GET" }
					);

					const data = await response.json();
					const isEnabled = data.enabled || false;

					// Update the featureFlagStatus with negotiation-specific data
					set((state) => ({
						featureFlagStatus: {
							...state.featureFlagStatus,
							[negotiationId]: {
								...(state.featureFlagStatus[negotiationId] || {}),
								[featureFlag]: isEnabled,
							},
						},
					}));

					return isEnabled;
				} catch (err) {
					const errorMessage =
						err instanceof Error
							? err.message
							: `Failed to check feature flag: ${featureFlag}`;
					error("Error", errorMessage);
					return false;
				}
			},

			fetchAvailableFlags: async () => {
				const url = import.meta.env.VITE_API_BASE_URL;
				const { error } = useToastStore.getState();

				try {
					const response = await fetchWithHeaders(
						`${url}api/v1/feature-flags/available`,
						{ method: "GET" }
					);

					const data = await response.json();
					set({ availableFlags: data.feature_flags || [] });
				} catch (err) {
					const errorMessage =
						err instanceof Error
							? err.message
							: "Failed to fetch available flags";
					error("Error", errorMessage);
				}
			},

			setFeatureFlag: async (
				negotiationId: number,
				featureFlag: string,
				enabled: boolean
			) => {
				const url = import.meta.env.VITE_API_BASE_URL;
				const { error, success } = useToastStore.getState();

				set({ loading: true, error: null });

				try {
					const response = await fetchWithHeaders(
						`${url}api/v1/feature-flags/${negotiationId}`,
						{
							method: "PUT",
							body: JSON.stringify({
								feature_flag: featureFlag,
								enabled: enabled,
							}),
						}
					);

					const data = await response.json();

					if (data.success) {
						// Update local state
						const currentFlags = get().featureFlags;
						let newFlags: string[];

						if (enabled) {
							newFlags = currentFlags.includes(featureFlag)
								? currentFlags
								: [...currentFlags, featureFlag];
						} else {
							newFlags = currentFlags.filter(
								(f) => f !== featureFlag
							);
						}

						set({ featureFlags: newFlags, loading: false });
						success(
							"Success",
							`Feature flag ${featureFlag} ${enabled ? "enabled" : "disabled"}`
						);
						return true;
					} else {
						error("Error", data.message || "Failed to update feature flag");
						set({ loading: false });
						return false;
					}
				} catch (err) {
					const errorMessage =
						err instanceof Error
							? err.message
							: "Failed to update feature flag";
					error("Error", errorMessage);
					set({ error: errorMessage, loading: false });
					return false;
				}
			},

			hasFeature: (featureFlag: string) => {
				return get().featureFlags.includes(featureFlag);
			},

			isFeatureEnabled: (negotiationId: number, featureFlag: string) => {
				return get().featureFlagStatus[negotiationId]?.[featureFlag] || false;
			},

			reset: () => {
				set({
					featureFlags: [],
					featureFlagStatus: {},
					loading: false,
					error: null,
				});
			},
		}),
		{ name: "feature-flags-store" }
	)
);
